/**
 * The app's end of a preview frame's port (PF1): it answers the page's `steddy.fs` and relative
 * `fetch()` from the tab's own executor, read-only. Every path is confined to /workspace (no `..`,
 * no other root), sizes are capped, and a message of any other shape gets no answer at all.
 */
import { SandboxError } from "../sandbox/core/errors";
import type { Executor, FileInfo } from "../sandbox/core/types";

/** What a preview may read: the read-only part of the executor of a workspace this tab runs. */
export type WorkspaceReader = Pick<Executor, "list" | "stat" | "exportFile">;

export const WORKSPACE_ROOT = "/workspace";
/** The page itself. */
export const PAGE_MAX_BYTES = 2 * 1024 * 1024;
/** One read or fetch. */
export const FILE_MAX_BYTES = 8 * 1024 * 1024;
export const LIST_MAX_ENTRIES = 1000;
/** Requests one frame may have open; more are refused, not queued. */
export const MAX_IN_FLIGHT = 8;
const PATH_MAX_CHARS = 1024;
const OPS = new Set(["read", "list", "stat", "fetch"]);
const FORBIDDEN_CHARS = /[\p{Cc}\p{Cf}\p{Cs}\\]/u;

const TYPES: Record<string, string> = {
  csv: "text/csv; charset=utf-8",
  tsv: "text/tab-separated-values; charset=utf-8",
  txt: "text/plain; charset=utf-8",
  md: "text/markdown; charset=utf-8",
  html: "text/html; charset=utf-8",
  htm: "text/html; charset=utf-8",
  css: "text/css; charset=utf-8",
  js: "text/javascript; charset=utf-8",
  mjs: "text/javascript; charset=utf-8",
  json: "application/json",
  geojson: "application/geo+json",
  svg: "image/svg+xml",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  pdf: "application/pdf",
  wasm: "application/wasm",
  parquet: "application/vnd.apache.parquet",
};

export type PreviewErrorCode = "ENOENT" | "EISDIR" | "EINVAL" | "E2BIG" | "EBUSY" | "EUNAVAILABLE";

export interface PreviewRequest {
  id: number;
  op: "read" | "list" | "stat" | "fetch";
  path: string;
}

export type PreviewReply =
  | { id: number; ok: true; result: unknown }
  | { id: number; ok: false; error: { code: PreviewErrorCode; message: string } };

class PreviewError extends Error {
  constructor(
    readonly code: PreviewErrorCode,
    message: string
  ) {
    super(message);
  }
}

export function dirOf(path: string): string {
  return path.slice(0, path.lastIndexOf("/")) || "/";
}

/**
 * `raw` as an absolute /workspace path: relative to `dir` (the page's folder), or absolute under
 * /workspace. Null for anything else: another root, a `..`, control characters, backslashes.
 */
export function confine(dir: string, raw: unknown): string | null {
  if (typeof raw !== "string" || raw.length > PATH_MAX_CHARS || FORBIDDEN_CHARS.test(raw)) {
    return null;
  }
  const text = raw.normalize("NFC");
  const absolute = text === WORKSPACE_ROOT || text.startsWith(`${WORKSPACE_ROOT}/`);
  if (text.startsWith("/") && !absolute) return null;
  const segments = (absolute ? text : `${dir}/${text}`).split("/").filter((s) => s && s !== ".");
  if (segments.includes("..") || segments[0] !== WORKSPACE_ROOT.slice(1)) return null;
  return `/${segments.join("/")}`;
}

/** The Content-Type a fetch answers with, from the file's extension. */
export function contentType(path: string): string {
  const name = path.slice(path.lastIndexOf("/") + 1);
  const dot = name.lastIndexOf(".");
  return (dot > 0 && TYPES[name.slice(dot + 1).toLowerCase()]) || "application/octet-stream";
}

function isRequest(value: unknown): value is PreviewRequest {
  const request = value as Partial<PreviewRequest> | null;
  return (
    !!request &&
    typeof request === "object" &&
    Number.isSafeInteger(request.id) &&
    typeof request.op === "string" &&
    OPS.has(request.op) &&
    typeof request.path === "string"
  );
}

/** A file's entry as the page sees it: no sync internals. */
function entry(info: FileInfo) {
  return { path: info.path, name: info.name, type: info.type, size: info.size, mtime: info.mtime };
}

/**
 * The file's bytes, refused past `max`: by its size before any read when the workspace knows it,
 * else (an export not loaded yet) by what the one download brought. Given the frame's `inFlight`,
 * those downloads go one at a time, each after the one before it.
 */
export async function fileBytes(
  reader: WorkspaceReader,
  path: string,
  max = FILE_MAX_BYTES,
  inFlight?: InFlight
): Promise<Uint8Array> {
  const info = await reader.stat(path);
  if (info.type !== "file") throw new PreviewError("EISDIR", `${path} is a folder`);
  if (info.size !== null && info.size > max) throw tooBig(path, max);
  const bytes = await (info.size === null && inFlight
    ? oneAtATime(inFlight, () => reader.exportFile(path))
    : reader.exportFile(path));
  if (bytes.byteLength > max) throw tooBig(path, max);
  return bytes;
}

function oneAtATime<T>(inFlight: InFlight, download: () => Promise<T>): Promise<T> {
  const turn = (inFlight.unknownSize ?? Promise.resolve()).then(download);
  inFlight.unknownSize = turn.then(
    () => undefined,
    () => undefined
  );
  return turn;
}

function tooBig(path: string, max: number) {
  return new PreviewError("E2BIG", `${path} is over ${Math.round(max / 1024 / 1024)} MB`);
}

async function answer(
  reader: WorkspaceReader,
  op: PreviewRequest["op"],
  path: string,
  inFlight: InFlight
) {
  switch (op) {
    case "stat":
      return { result: entry(await reader.stat(path)) };
    case "list": {
      // The executor lists a folder whole; the cap applies to what it gave.
      const entries = await reader.list(path);
      if (entries.length > LIST_MAX_ENTRIES) {
        throw new PreviewError("E2BIG", `${path} has over ${LIST_MAX_ENTRIES} entries`);
      }
      return { result: entries.map(entry) };
    }
    case "read":
      return {
        result: new TextDecoder().decode(await fileBytes(reader, path, FILE_MAX_BYTES, inFlight)),
      };
    case "fetch": {
      // A copy of its own: the executor's buffer is never handed to the page.
      const bytes = (await fileBytes(reader, path, FILE_MAX_BYTES, inFlight)).slice().buffer;
      return { result: { bytes, type: contentType(path) }, transfer: [bytes] };
    }
  }
}

function failure(error: unknown): { code: PreviewErrorCode; message: string } {
  if (error instanceof PreviewError) return { code: error.code, message: error.message };
  if (error instanceof SandboxError && (error.code === "ENOENT" || error.code === "EISDIR")) {
    return { code: error.code, message: error.message };
  }
  if (error instanceof SandboxError && (error.code === "ENOTDIR" || error.code === "EINVAL")) {
    return { code: "EINVAL", message: error.message };
  }
  return { code: "EUNAVAILABLE", message: "The workspace could not be read here" };
}

/** The requests a frame has open, shared by every port it is given. */
export interface InFlight {
  open: number;
  /** The frame's latest unknown-size download (settled either way): the next waits for it. */
  unknownSize?: Promise<void>;
}

/**
 * Answers the requests that arrive on `port`, for a page in `dir`. `reader` is asked each time:
 * null once this tab no longer runs the workspace. `inFlight` is the frame's count, so a frame
 * holds MAX_IN_FLIGHT at most however many ports it has had. Returns a function that closes the
 * port.
 */
export function servePreviewPort(
  port: MessagePort,
  dir: string,
  reader: () => WorkspaceReader | null,
  inFlight: InFlight = { open: 0 }
): () => void {
  let closed = false;
  const reply = (message: PreviewReply, transfer: Transferable[] = []) => {
    if (!closed) port.postMessage(message, transfer);
  };
  port.onmessage = (event: MessageEvent) => {
    const request = event.data;
    if (closed || !isRequest(request)) return; // malformed: no answer
    const { id, op } = request;
    const path = confine(dir, request.path);
    if (path === null) {
      reply({ id, ok: false, error: { code: "EINVAL", message: "Only /workspace can be read" } });
      return;
    }
    if (inFlight.open >= MAX_IN_FLIGHT) {
      reply({ id, ok: false, error: { code: "EBUSY", message: "Too many reads at once" } });
      return;
    }
    const current = reader();
    if (!current) {
      reply({ id, ok: false, error: { code: "EUNAVAILABLE", message: "The sandbox stopped" } });
      return;
    }
    inFlight.open++;
    void answer(current, op, path, inFlight)
      .then(
        ({ result, transfer }) => reply({ id, ok: true, result }, transfer),
        (error: unknown) => reply({ id, ok: false, error: failure(error) })
      )
      .finally(() => inFlight.open--);
  };
  port.start();
  return () => {
    closed = true;
    port.onmessage = null;
    port.close();
  };
}
